import "./App.css"
import {Route, Routes, useNavigate} from "react-router-dom";
import Login from "./Login";
import Register from "./Register";
import React, {useEffect, useRef, useState} from "react";
import WebGLAppContainer from "./ViewerApp";
import GetAccessToken from "./Tokens";
import ModelList from "./ModelList";
import {openDB} from 'idb';
import blankImage from "./icons/blank.png"
import {useConfig} from "./ConfigContext";
import AdminPanel from "./AdminPanel";
import embryoImage from "./logo/embryo.png";
import embryoTitle from "./logo/3Dembryo.png";
import footerImage from "./logo/footer.png";
import About from "./About";

async function openImageDB() {
    return await openDB('imageDB', 1, {
        upgrade(db) {
            if (!db.objectStoreNames.contains('images')) {
                db.createObjectStore('images', { keyPath: 'group_id' });
            }
        },
    });
}

async function getImageFromDB(group_id) {
    const db = await openImageDB();
    return await db.get('images', group_id);
}

async function saveImageToDB(group_id, blob, etag) {
    const db = await openImageDB();
    await db.put('images', { group_id, blob, etag });
    return { group_id, blob, etag };
}

function GroupElement({index, group_id, group_name, group_description, groupServiceUrl, setIsSpecimenListOpen, setSelectedGroup}) {
    const navigate = useNavigate();
    const [imageSrc, setImageSrc] = useState(null);
    const config = useConfig();

    useEffect(() => {
        const fetchData = async () => {
            try {
                const cachedImage = await getImageFromDB(group_id);
                let cachedEtag = "";
                if (cachedImage) {
                    cachedEtag = cachedImage.etag;
                }
                const response = await fetch(
                    `${groupServiceUrl}/public/group/image?group_id=${group_id}`,
                    {
                        headers: {
                            'If-None-Match': cachedEtag,
                        },
                    }
                );
                if (response.status === 304) {
                    const imageUrl = URL.createObjectURL(cachedImage.blob);
                    setImageSrc(imageUrl);
                    return;
                }
                if (response.ok) {
                    const blob = await response.blob();
                    const etag = response.headers.get('ETag');
                    const storedImage = await saveImageToDB(group_id, blob, etag);
                    const imageUrl = URL.createObjectURL(storedImage.blob);
                    setImageSrc(imageUrl);
                } else {
                    console.error('Failed to fetch image:', response.statusText);
                }
            } catch (error) {
                if (error.message !== 'Unauthorized') {
                    console.error('Error fetching the image:', error);
                }
            }
        };

        fetchData();
    }, [groupServiceUrl]);


    const handleGroupClick = () => {
        setIsSpecimenListOpen(true);
        setSelectedGroup({group_id : group_id, group_name: group_name});
    };

    return (
        <div onClick={(e) => {
            e.stopPropagation();
            handleGroupClick()}}
             className="grid-item" key={index}>
            <img src={imageSrc || blankImage}
                 alt={group_name}
                 className="thumbnail"
                 onError={(e) => e.target.src = blankImage}/>
            <h3>{group_name}</h3>
            <p>{group_description}</p>
        </div>
    )
}

function HomePage() {
    const [groups, setGroups] = useState(null);
    const [groupServiceUrl, setGroupServiceUrl] = useState(null);
    const [specimenServiceUrl, setSpecimenServiceUrl] = useState(null);
    const navigate = useNavigate();
    const [isSpecimenListOpen, setIsSpecimenListOpen] = useState(false);
    const [selectedGroup, setSelectedGroup] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [searchedSpecimens, setSearchedSpecimens] = useState(null);
    const listRef = useRef(null);
    const config = useConfig();

    useEffect(() => {
        if (config) {
            setGroupServiceUrl(config["group_service_url"]);
            setSpecimenServiceUrl(config["model_service_url"]);
        }
    }, [config]);


    useEffect(() => {
        const handleClickOutside = (event) => {
            if (listRef.current && !listRef.current.contains(event.target)) {
                setIsSpecimenListOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    useEffect(() => {
        if (groupServiceUrl == null)
            return;

        const fetchData = async () => {
            fetch(groupServiceUrl + '/public/list?include_description=true', {
            }).then(async response => {
                if (response.status === 401) {
                    sessionStorage.removeItem('access_token');
                    console.error('Unauthorized:', response.statusText);
                    await fetchData();
                }
                return response.json();
            })
                .then(data => {
                    setGroups(data["groups"]);
                })
                .catch(error => {
                    if (error.message !== 'Unauthorized') {
                        console.error('Error fetching the models:', error);
                    }
                });
        };

        fetchData();
    }, [groupServiceUrl]);

    const setSpecimen = (specimen) => {
        navigate("/viewer?name=" + specimen);
    };

    const handleSearch = async () => {
        if (searchTerm.trim() === "") {
            setSearchedSpecimens(null);
            return;
        }

        try {
            const response = await fetch(`${specimenServiceUrl}/public/search?keyword=${searchTerm}`, {
            });

            if (response.ok) {
                const data = await response.json();
                setSearchedSpecimens(data.specimens);
            } else {
                console.error('Failed to fetch specimens:', response.statusText);
            }
        } catch (error) {
            console.error('Error searching for specimens:', error);
        }
    };

    const filteredGroups = groups && searchedSpecimens ? groups.filter(group =>
        searchedSpecimens.some(specimen => specimen.group_name === group.group_name)
    ) : groups;

    const searchedSpecimenNames = searchedSpecimens ? searchedSpecimens.filter(specimen => specimen.group_name === selectedGroup.group_name).map(specimen => specimen.specimen_name) : undefined;

    return (
        <div className={"main-grid-container"} style={{height: "100dvh"}}>
            <div className={"logo-and-grid-container"} style={{minHeight: "100dvh"}}>
                <img src={embryoImage} alt="Embryo" className="embryo-image" />

                <div className={"grid-and-search-container"}>
                    <div className="logo">
                        <img src={embryoTitle} alt={"3Dembryo"} style={{width: "350px", height: "100%"}}/>
                    </div>

                    {isSpecimenListOpen && <div ref={listRef} className={"model-list-container"}><ModelList setModel={setSpecimen} group_id={selectedGroup.group_id} specimenList={searchedSpecimenNames} listTitle={"Specimens in " + selectedGroup.group_name}></ModelList></div>}
                    <div className={"tag-search"}>
                        <input
                            type="text"
                            placeholder="Search specimens..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="search-bar"
                        />
                        <button onClick={handleSearch} className="search-button">Search</button>
                    </div>
                    <div className="grid-container">
                        {filteredGroups && filteredGroups.map((group, index) => (
                            <GroupElement
                                key={index}
                                index={index}
                                group_id={group.group_id}
                                group_name={group.group_name}
                                group_description={group.description}
                                groupServiceUrl={groupServiceUrl}
                                setIsSpecimenListOpen={setIsSpecimenListOpen}
                                setSelectedGroup={setSelectedGroup}
                            />
                        ))}
                    </div>

                </div>
            </div>

            <footer className="footer">
                <img src={footerImage} alt="footer" className="footer-image" />
                <div className="footer-text">
                    <p>This website is maintained by <a href="#">Jill Hikspoors</a>, 3Dembryo group, Maastricht University.</p>
                    <p>
                        All site content, unless otherwise specified, is licensed under a <a href="#">Creative Commons Attribution License</a>.
                        <a href="#"> Please refer to the terms of use.</a> | <a href="/about">About Us</a>
                    </p>
                </div>
            </footer>
        </div>
    );
}

const App = () => {
    return (
        <Routes style={{overflow: "hidden", height: "1200dvh"}}>
            <Route path="/" element={<HomePage />} />
            <Route path="/admin" element={<AdminPanel />} />
            <Route path="/viewer" element={<WebGLAppContainer />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/about" element={<About />} />
        </Routes>
    );
};

export default App;
